import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Switch,
} from "@mui/material";
import clsx from "clsx";
import { FC, useEffect, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import styled from "styled-components";

import { useBulkEditFormContext, useDynamicFormsTranslate } from "@ryde/redux";
import {
  DATA_E2E_KEY,
  ICustomField,
  IStyledAttributes,
  parseBoolean,
  switchAnyValueToBool,
  ValidationHelpers,
} from "@ryde/shared";

import { ControlErrorDisplay } from "../parts-styled";
import { FieldExplanationIcon } from "./_parts";

const SwitchTemplateWrapper = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "switch-template-wrapper",
})`
  position: relative;
  width: 100%;
  overflow: visible;
`;

interface ISwitchTemplateProps extends ICustomField {
  className?: string;
}

export const SwitchTemplate: FC<ISwitchTemplateProps> = ({
  id,
  name: fieldName,
  formLabel,
  labelTranslated,
  formAttributes,
  formValidation,
  className,
  size = "medium",
  labelPlacement = "end",
}) => {
  const {
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();
  const { isBulkEditForm } = useBulkEditFormContext();
  const switchExplicitFalseValue = formAttributes?.switchExplicitFalseValue;
  const switchExplicitFalseValueComputed = isBulkEditForm
    ? true
    : switchExplicitFalseValue;
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name: fieldName,
    fallbackText: formLabel,
    translatedText: labelTranslated,
  });
  const requiredComputed = useMemo(() => {
    return ValidationHelpers.hasRequiredRule(formValidation);
  }, [formValidation]);

  useEffect(() => {
    const raw = getValues(fieldName);

    if (switchExplicitFalseValueComputed && raw === undefined) {
      setValue(fieldName, false);
      return;
    }

    if (raw === undefined || raw === null) {
      return;
    }

    if (typeof raw === "boolean") {
      return;
    }
    //normalize initial value if it's untypical case like string "true" or "on"
    setValue(fieldName, parseBoolean(raw), {
      shouldDirty: false,
      shouldTouch: false,
    });
  }, [switchExplicitFalseValueComputed, fieldName, setValue, getValues]);

  return (
    <SwitchTemplateWrapper>
      <Controller
        control={control}
        name={fieldName}
        render={({
          field: { value, ref, onChange, ...rest },
          fieldState: { error },
        }) => {
          return (
            <FormControl
              className={clsx(className)}
              required={requiredComputed}
              data-e2e-template="switch-template"
              data-e2e-field-name={fieldName}
              error={!!error}
            >
              <FormControlLabel
                {...rest}
                onClick={e => e.preventDefault()}
                labelPlacement={labelPlacement}
                control={
                  <Switch
                    size={size}
                    data-e2e-id={`${fieldName}_${id || ""}`}
                    slotProps={{ input: { ref } }}
                    onClick={e => e.stopPropagation()}
                  />
                }
                label={formLabelTranslated}
                checked={switchAnyValueToBool(value)}
                onChange={(_, checked) =>
                  onChange(
                    switchExplicitFalseValueComputed ? !!checked : checked,
                  )
                }
                disabled={formAttributes?.disabled}
              />
              {error && (
                <FormHelperText data-e2e="switch-template-errors">
                  {error.message}
                </FormHelperText>
              )}
            </FormControl>
          );
        }}
      />
      <FieldExplanationIcon formAttributes={formAttributes} />
      <ControlErrorDisplay errors={errors} fieldName={fieldName} />
    </SwitchTemplateWrapper>
  );
};
